import type { FC } from 'react';

import React from 'react';

import { formatAmount } from 'src/utils/money';

import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';

interface CurrencyBalanceCardProps {
  balance: string;
  symbol: string;
  logoURI?: string | null;
  name: string;
}

const GRADIENT_COLORS = [
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
];

export const CurrencyBalanceCard: FC<CurrencyBalanceCardProps> = ({
  balance,
  symbol,
  logoURI,
  name,
}) => {
  // Use symbol to deterministically select a gradient
  const gradientIndex = symbol.charCodeAt(0) % GRADIENT_COLORS.length;
  const gradient = GRADIENT_COLORS[gradientIndex];

  // Up to 4 decimal places, trailing zeros dropped, through the shared en-US formatter.
  const formattedBalance = formatAmount(parseFloat(balance), 4);

  return (
    <Stack
      gap={2}
      p={4}
      alignItems="center"
      justifyContent="center"
      style={{
        background: gradient,
        borderRadius: '16px',
        minHeight: '140px',
        flex: '1 1 0',
        minWidth: '200px',
      }}
    >
      <Stack flexDirection="row" alignItems="center" gap={1}>
        {logoURI ? (
          <img
            src={logoURI}
            alt={name}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.3)',
            }}
          >
            <Typography variant="large" fontWeight="700" color="white">
              {symbol.charAt(0)}
            </Typography>
          </Stack>
        )}
        <Typography variant="title3" fontWeight="700" color="white">
          {symbol}
        </Typography>
      </Stack>
      <Typography variant="title1" fontWeight="700" color="white">
        {formattedBalance}
      </Typography>
    </Stack>
  );
};
